import { useMemo, useState } from 'react';

import type { LHCDetailInvoiceRowData } from '../types';
import type { MutationHistoryFilterLabel } from '../utils/lhc-mutation-history';
import { getSapNoBiltoFromRow } from '../utils/lhc-mutation-history';

export type MutasiModalMode = 'search' | 'change' | 'add';

export const getMutasiModalLabels = (
  mode: MutasiModalMode
): {
  title: string | undefined;
  submitLabel: string | undefined;
} => {
  switch (mode) {
    case 'change':
      return {
        title: 'Ganti Nomor Mutasi',
        submitLabel: 'Ganti Mutasi',
      };
    case 'add':
      return {
        title: 'Tambah Nomor Mutasi',
        submitLabel: 'Tambah Mutasi',
      };
    case 'search':
      return {
        title: undefined,
        submitLabel: undefined,
      };
  }
};

export function useLhcDetailModals() {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSearchMutasiModalOpen, setIsSearchMutasiModalOpen] = useState(false);
  const [isPaymentProofDrawerOpen, setIsPaymentProofDrawerOpen] =
    useState(false);
  const [isMutationHistoryDrawerOpen, setIsMutationHistoryDrawerOpen] =
    useState(false);
  const [selectedBiltoForHistoryDrawer, setSelectedBiltoForHistoryDrawer] =
    useState<string | null>(null);
  const [mutationHistoryFilter, setMutationHistoryFilter] =
    useState<MutationHistoryFilterLabel>('Semua');
  const [mutationHistoryStartDate, setMutationHistoryStartDate] = useState('');
  const [mutationHistoryEndDate, setMutationHistoryEndDate] = useState('');
  const [mutationHistoryPage, setMutationHistoryPage] = useState(1);
  const [mutationHistoryLimit, setMutationHistoryLimit] = useState(10);
  const [selectedRowForMutasi, setSelectedRowForMutasi] =
    useState<LHCDetailInvoiceRowData | null>(null);
  const [mutasiModalMode, setMutasiModalMode] =
    useState<MutasiModalMode>('search');
  const [selectedRowForDrawer, setSelectedRowForDrawer] =
    useState<LHCDetailInvoiceRowData | null>(null);
  const [selectedBankMutationIdForDetail, setSelectedBankMutationIdForDetail] =
    useState<string | null>(null);
  const [selectedRowForRemark, setSelectedRowForRemark] =
    useState<LHCDetailInvoiceRowData | null>(null);
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);

  const mutasiModalLabels = useMemo(() => {
    return getMutasiModalLabels(mutasiModalMode);
  }, [mutasiModalMode]);

  const openSearchModal = () => {
    setIsSearchModalOpen(true);
  };

  const closeSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const openSearchMutation = (row: LHCDetailInvoiceRowData) => {
    setMutasiModalMode('search');
    setSelectedRowForMutasi(row);
    setIsSearchMutasiModalOpen(true);
  };

  const openChangeMutation = (row: LHCDetailInvoiceRowData) => {
    setMutasiModalMode('change');
    setSelectedRowForMutasi(row);
    setIsSearchMutasiModalOpen(true);
  };

  const openAddMutation = (row: LHCDetailInvoiceRowData) => {
    setMutasiModalMode('add');
    setSelectedRowForMutasi(row);
    setIsSearchMutasiModalOpen(true);
  };

  const closeSearchMutasiModal = () => {
    setIsSearchMutasiModalOpen(false);
    setSelectedRowForMutasi(null);
    setMutasiModalMode('search');
  };

  const openPaymentProofDrawer = (
    row: LHCDetailInvoiceRowData,
    bankMutationId?: string
  ) => {
    setSelectedRowForDrawer(row);
    setSelectedBankMutationIdForDetail(bankMutationId ?? null);
    setIsPaymentProofDrawerOpen(true);
  };

  const closePaymentProofDrawer = () => {
    setIsPaymentProofDrawerOpen(false);
    setSelectedRowForDrawer(null);
    setSelectedBankMutationIdForDetail(null);
  };

  const openMutationHistoryDrawer = (row: LHCDetailInvoiceRowData) => {
    const sapNoBilto = getSapNoBiltoFromRow(row);
    if (!sapNoBilto) {
      return;
    }

    setSelectedBiltoForHistoryDrawer(sapNoBilto);
    setMutationHistoryPage(1);
    setIsMutationHistoryDrawerOpen(true);
  };

  const closeMutationHistoryDrawer = () => {
    setIsMutationHistoryDrawerOpen(false);
    setSelectedBiltoForHistoryDrawer(null);
    setMutationHistoryFilter('Semua');
    setMutationHistoryStartDate('');
    setMutationHistoryEndDate('');
    setMutationHistoryPage(1);
    setMutationHistoryLimit(10);
  };

  const openRemarkModal = (row: LHCDetailInvoiceRowData) => {
    setSelectedRowForRemark(row);
    setIsRemarkModalOpen(true);
  };

  const closeRemarkModal = () => {
    setIsRemarkModalOpen(false);
    setSelectedRowForRemark(null);
  };

  return {
    closeMutationHistoryDrawer,
    closePaymentProofDrawer,
    closeRemarkModal,
    closeSearchModal,
    closeSearchMutasiModal,
    isMutationHistoryDrawerOpen,
    isPaymentProofDrawerOpen,
    isRemarkModalOpen,
    isSearchModalOpen,
    isSearchMutasiModalOpen,
    mutasiModalLabels,
    mutasiModalMode,
    mutationHistoryEndDate,
    mutationHistoryFilter,
    mutationHistoryLimit,
    mutationHistoryPage,
    mutationHistoryStartDate,
    openAddMutation,
    openChangeMutation,
    openMutationHistoryDrawer,
    openPaymentProofDrawer,
    openRemarkModal,
    openSearchModal,
    openSearchMutation,
    selectedBiltoForHistoryDrawer,
    selectedBankMutationIdForDetail,
    selectedRowForDrawer,
    selectedRowForMutasi,
    selectedRowForRemark,
    setMutationHistoryEndDate,
    setMutationHistoryFilter,
    setMutationHistoryLimit,
    setMutationHistoryPage,
    setMutationHistoryStartDate,
  };
}
